import React, { useMemo, useState } from 'react';
import { Modal, Button, Typography, Space, Card, Tag, Spin, Alert, theme, App } from 'antd';
import { BuildOutlined, TeamOutlined, CheckOutlined, RightOutlined } from '@ant-design/icons';
import { useTenant } from '@/hooks/useTenant';
import type { TenantMembership, CompanyMembership } from '@/api/auth/interfaces';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/utils/apiError';

const { Text } = Typography;

export interface TenantSelectorProps {
  open: boolean;
  onClose: () => void;
  onTenantSelected: (tenant: { clientId?: string; companyId?: string }) => void;
  title?: string;
  description?: string;
}

type SelectableOption =
  | { type: 'client'; client: TenantMembership }
  | { type: 'company'; company: CompanyMembership; underTenant: TenantMembership | null };

export const TenantSelector: React.FC<TenantSelectorProps> = ({
  open,
  onClose,
  onTenantSelected,
  title: titleProp,
  description: descriptionProp
}) => {
  const { t } = useTranslation('common');
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const title = titleProp ?? t('ui.tenantSelector.title');
  const description = descriptionProp ?? t('ui.tenantSelector.description');
  const { memberships, membershipsLoading, switchToTenant, isSwitching } = useTenant();
  const [selectedTenant, setSelectedTenant] = useState<{ clientId?: string; companyId?: string } | null>(null);

  const handleConfirm = async () => {
    if (!selectedTenant) return;

    try {
      await switchToTenant(selectedTenant);
      onTenantSelected(selectedTenant);
      onClose();
    } catch (error: any) {
      const msg = getApiErrorMessage(error, t('ui.tenantSelector.switchError'));
      message.error(msg);
    }
  };

  const groupedOptions = useMemo((): SelectableOption[] => {
    if (!memberships?.clients?.length && !memberships?.companies?.length) return [];

    const clients = memberships.clients || [];
    const companies = memberships.companies || [];
    const options: SelectableOption[] = [];

    clients.forEach((client: TenantMembership) => {
      options.push({ type: 'client', client });
      const childCompanies = companies.filter(
        (c: CompanyMembership) => c.clientId === client.clientId
      );
      childCompanies.forEach((company: CompanyMembership) => {
        options.push({ type: 'company', company, underTenant: client });
      });
    });

    const clientIds = new Set(clients.map((c: TenantMembership) => c.clientId));
    companies
      .filter((c: CompanyMembership) => !clientIds.has(c.clientId))
      .forEach((company: CompanyMembership) => {
        options.push({ type: 'company', company, underTenant: null });
      });

    return options;
  }, [memberships]);

  const totalCount = useMemo(() => {
    if (!memberships) return 0;
    return (memberships.clients?.length ?? 0) + (memberships.companies?.length ?? 0);
  }, [memberships]);

  const isSelected = (opt: SelectableOption) => {
    if (!selectedTenant) return false;
    if (opt.type === 'client') return selectedTenant.clientId === opt.client.clientId;
    return selectedTenant.companyId === opt.company.companyId;
  };

  const handleSelect = (opt: SelectableOption) => {
    if (opt.type === 'client') {
      setSelectedTenant({ clientId: opt.client.clientId });
    } else {
      setSelectedTenant({ companyId: opt.company.companyId });
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('ui.tenantSelector.cancel')}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={isSwitching}
          disabled={!selectedTenant}
          onClick={handleConfirm}
        >
          {t('ui.tenantSelector.continue')}
        </Button>
      ]}
      width={560}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">{description}</Text>

        {membershipsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">{t('ui.tenantSelector.loadingOrganizations')}</Text>
            </div>
          </div>
        ) : totalCount === 0 ? (
          <Alert
            title={t('ui.tenantSelector.noOrganizationsTitle')}
            description={t('ui.tenantSelector.noOrganizationsDescription')}
            type="info"
            showIcon
          />
        ) : (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              {t('ui.tenantSelector.availableOrganizations', { count: totalCount })}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 12, display: 'block' }}>
              {t('ui.tenantSelector.organizationHint')}
              {totalCount === 1 && ` ${t('ui.tenantSelector.selectToContinue')}`}
            </Text>

            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {groupedOptions.map((opt) => {
                if (opt.type === 'client') {
                  const name = opt.client.clientName?.trim() || opt.client.clientId;
                  const selected = isSelected(opt);
                  return (
                    <Card
                      key={`client-${opt.client.clientId}`}
                      size="small"
                      hoverable
                      onClick={() => handleSelect(opt)}
                      style={{
                        border: selected ? `2px solid ${token.colorPrimary}` : `1px solid ${token.colorBorder}`,
                        backgroundColor: selected ? token.colorPrimaryBg : undefined,
                        cursor: 'pointer'
                      }}
                    >
                      <Space>
                        <Tag color="blue" icon={<BuildOutlined />}>
                          {t('ui.tenantSelector.tenant')}
                        </Tag>
                        <Text strong>{name}</Text>
                        {opt.client.roles.map((role) => (
                          <Tag key={role} color="blue">
                            {role}
                          </Tag>
                        ))}
                        {selected && <CheckOutlined style={{ color: token.colorPrimary }} />}
                      </Space>
                    </Card>
                  );
                }

                const name = opt.company.companyName?.trim() || opt.company.companyId;
                const selected = isSelected(opt);
                const tenantName = opt.underTenant
                  ? (opt.underTenant.clientName?.trim() || opt.underTenant.clientId)
                  : null;

                return (
                  <Card
                    key={`company-${opt.company.companyId}`}
                    size="small"
                    hoverable
                    onClick={() => handleSelect(opt)}
                    style={{
                      marginLeft: 20,
                      borderLeft: `3px solid ${token.colorSuccess}`,
                      border: selected ? `2px solid ${token.colorPrimary}` : `1px solid ${token.colorBorder}`,
                      backgroundColor: selected ? token.colorPrimaryBg : undefined,
                      cursor: 'pointer'
                    }}
                  >
                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                      {tenantName && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <RightOutlined style={{ marginRight: 4 }} />
                          {t('ui.tenantSelector.tenant')}: {tenantName}
                        </Text>
                      )}
                      <Space wrap>
                        <Tag color="green" icon={<TeamOutlined />}>
                          {t('ui.tenantSelector.company')}
                        </Tag>
                        <Text strong>{name}</Text>
                        {opt.company.roles.map((role) => (
                          <Tag key={role} color="blue">
                            {role}
                          </Tag>
                        ))}
                        {selected && <CheckOutlined style={{ color: token.colorPrimary }} />}
                      </Space>
                    </Space>
                  </Card>
                );
              })}
            </Space>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default TenantSelector;
